import React, {createContext, useContext, useEffect, useMemo, useReducer, useRef} from 'react'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'
import {useOverlayActions} from '../../../contexts/overlay-manager'
import useLenisScrollLock from '../../../hooks/use-lenis-scroll-lock'
import useNativeScrollLock from '../../../hooks/use-native-scroll-lock'

/**
 * # Navigation header state
 *
 * Holds the Xleap navigation header's open/close flag. State lives in the
 * reducer below; the provider layers on four cross-cutting behaviours: opening
 * closes every registered overlay (exclusivity), a navigation closes the nav,
 * Escape closes it while open, and body scroll is locked while it stays open.
 *
 * ## Overlay-manager exception
 *
 * Per `docs/agents/overlays.md` all overlays MUST use `createOverlayType` from
 * `app/contexts/overlay-manager.jsx`. This nav open-state deliberately does NOT:
 * it is an inline, in-header disclosure, not a portaled surface at the app root,
 * which is all `createOverlayType` can render. Exception approved by the tech
 * owner. Cross-overlay exclusivity is achieved cooperatively by calling
 * `useOverlayActions().closeAll()` when the nav opens.
 */

const OPEN = 'OPEN'
const CLOSE = 'CLOSE'
const TOGGLE = 'TOGGLE'
const INTENT = 'INTENT'

/**
 * `intent` records that the user has signalled they may open the nav (pointer
 * over the trigger, or focus on it) so the tree can be fetched before the click
 * lands. It is monotonic: once set it stays set, because the fetch it unblocks
 * is cached for the rest of the page's life and re-gating it would only throw
 * that away.
 *
 * @typedef {{ open: boolean, intent: boolean }} NavigationState
 */

/** @type {NavigationState} */
const initialState = {open: false, intent: false}

/**
 * @param {NavigationState} state
 * @param {{ type: string }} action
 * @returns {NavigationState}
 */
const reducer = (state, action) => {
    switch (action.type) {
        case OPEN:
            return state.open ? state : {...state, open: true, intent: true}
        case CLOSE:
            return state.open ? {...state, open: false} : state
        case TOGGLE:
            return {...state, open: !state.open, intent: true}
        case INTENT:
            return state.intent ? state : {...state, intent: true}
        default:
            return state
    }
}

const NavigationContext = createContext(null)

/**
 * Provides the navigation header open/close state.
 * Must wrap any subtree that consumes `useNavigationState`, and must itself sit
 * inside `<OverlayProvider>` (for `closeAll`) and a Router (for `useLocation`).
 *
 * @param {{ children: React.ReactNode }} props
 */
export const NavigationProvider = ({children}) => {
    const [state, dispatch] = useReducer(reducer, initialState)
    // The backdrop is a sibling of the flyout, not a child, so the close cannot
    // reach it by walking its own tree — and it has to know whether the backdrop
    // is holding focus it is about to take away (STOK-17609).
    const backdropRef = useRef(null)
    const {closeAll} = useOverlayActions()
    const location = useLocation()

    // Inline disclosure, not a Chakra overlay, so it locks scroll itself:
    // lock the body and pause Lenis while open; both release on close.
    useNativeScrollLock(state.open)
    useLenisScrollLock(state.open)

    const actions = useMemo(
        () => ({
            open: () => dispatch({type: OPEN}),
            close: () => dispatch({type: CLOSE}),
            toggle: () => dispatch({type: TOGGLE}),
            prefetch: () => dispatch({type: INTENT})
        }),
        []
    )

    // Cross-overlay exclusivity: opening the nav closes every registered overlay.
    useEffect(() => {
        if (state.open) {
            closeAll()
        }
    }, [state.open, closeAll])

    // Route-close: any navigation collapses the nav. Keyed on the whole
    // location, because a link to the page already open pushes an entry that
    // leaves the pathname untouched (STOK-17620).
    useEffect(() => {
        dispatch({type: CLOSE})
    }, [location])

    // Escape closes the nav; the listener is only attached while open.
    useEffect(() => {
        if (!state.open) return undefined
        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                dispatch({type: CLOSE})
            }
        }
        document.addEventListener('keydown', onKeyDown)
        return () => document.removeEventListener('keydown', onKeyDown)
    }, [state.open])

    const value = useMemo(
        () => ({open: state.open, intent: state.intent, actions, backdropRef}),
        [state.open, state.intent, actions]
    )

    return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>
}

NavigationProvider.propTypes = {
    children: PropTypes.node
}

/**
 * Reads the navigation header state and actions.
 * Named `useNavigationState` to avoid colliding with the unrelated
 * `app/hooks/use-navigation.js` default export.
 *
 * @returns {{ open: boolean, intent: boolean,
 *   backdropRef: React.RefObject<HTMLElement>, actions: {
 *   open: () => void, close: () => void, toggle: () => void,
 *   prefetch: () => void } }}
 */
export const useNavigationState = () => {
    const context = useContext(NavigationContext)

    if (!context) {
        throw new Error('useNavigationState must be used inside <NavigationProvider>')
    }

    return context
}
